import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ElementRef,
  HostListener,
  ViewEncapsulation
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { QuillEditorComponent } from 'ngx-quill';
import * as HtmlDiffModule from 'htmldiff-js';
import { ChannelMessageEntityExtendedClient, IzzyMetadataEngine } from 'mj_generatedentities';
import { IzzyWritingAssistService, WritingAssistMenuItem, WRITING_ASSIST_MENU, WRITING_ASSIST_MAX_THREAD_CONTEXT_CHARS } from '../../services/izzy-writing-assist.service';
import { WritingAssistPanelState } from '../writing-assist-menu/writing-assist-menu.component';
const HtmlDiff = HtmlDiffModule.default;

/** The Quill instance ngx-quill hands us (typed through the component so no direct quill import is needed). */
type QuillInstance = NonNullable<QuillEditorComponent['quillEditor']>;

/** Organization setting that hides the assistant (server enforces it too). */
export const WRITING_ASSISTANT_SETTING_KEY = 'WritingAssistantEnabled';

/**
 * Editor mode types
 */
export type EditorMode = 'edit' | 'diff' | 'split';

/**
 * Event emitted when content changes
 */
export interface ContentChangeEvent {
  content: string;
  hasChanges: boolean;
}

/**
 * Event emitted when user saves
 */
export interface SaveEvent {
  content: string;
  approved: boolean;
}

/**
 * Expanded Message Editor Component
 *
 * A full-featured WYSIWYG HTML editor with track changes visualization.
 * Features:
 * - Quill-based rich text editing
 * - HTML diff visualization using htmldiff-js
 * - Split view mode (editor + diff side by side)
 * - Expand to full component area
 * - Revert to original functionality
 */
@Component({
  standalone: false,
  selector: 'izzy-message-editor',
  templateUrl: './message-editor.component.html',
  styleUrls: ['./message-editor.component.css'],
  // Use None to allow Quill styles to work properly
  encapsulation: ViewEncapsulation.None
})
export class MessageEditorComponent implements OnInit, OnChanges, OnDestroy {

  // ========== Inputs ==========

  /** The original/generated content (base for diff comparison) */
  @Input() originalContent: string = '';

  /** The current/edited content */
  @Input() currentContent: string = '';

  /** Whether the editor is in expanded (full area) mode */
  @Input() isExpanded: boolean = false;

  /** Whether the content is read-only (view mode) */
  @Input() readOnly: boolean = false;

  /** Message format hint for the editor */
  @Input() messageFormat: 'HTML' | 'PlainText' | 'Markdown' = 'HTML';

  /**
   * Compact mode: just the Quill editor (toolbar + body), no mode/stat header and no
   * Revert/Cancel/Save footer. For composers whose host owns the submit button (Compose New,
   * ticket Reply). The AI assistant and track-changes baseline still work.
   */
  @Input() compact: boolean = false;

  /** Placeholder shown in the empty editor. */
  @Input() placeholder: string = 'Enter your message...';

  // ---- AI writing assistant (sparkle button in the toolbar) ----

  /** Channel the message belongs to (voice, AI keys, feature setting). Required for the assistant. */
  @Input() assistChannelId: string | null = null;

  /** The channel message being edited, when one exists (preferred org-scope proof on the server). */
  @Input() assistChannelMessageId: string | null = null;

  /** The message being replied to (HTML or text); passed as plain text to "Help me write" only. */
  @Input() assistThreadContext: string | null = null;

  // ========== Outputs ==========

  /** Emitted when content changes */
  @Output() contentChange = new EventEmitter<ContentChangeEvent>();

  /** Emitted when user clicks save/approve */
  @Output() save = new EventEmitter<SaveEvent>();

  /** Emitted when user clicks save draft */
  @Output() saveDraft = new EventEmitter<string>();

  /** Emitted when user cancels editing */
  @Output() cancel = new EventEmitter<void>();

  /** Emitted when expand/collapse state changes */
  @Output() expandChange = new EventEmitter<boolean>();

  /** Emitted when user clicks revert */
  @Output() revert = new EventEmitter<void>();

  // ========== ViewChild ==========

  @ViewChild('quillEditor') quillEditor?: QuillEditorComponent;
  @ViewChild('editorContainer') editorContainer?: ElementRef;
  @ViewChild('assistBarEl') assistBarEl?: ElementRef<HTMLElement>;

  // ========== Component State ==========

  /** Current editor mode */
  mode: EditorMode = 'edit';

  /** The content being edited */
  editedContent: string = '';

  /**
   * The original content normalized by Quill.
   * This is set once when the editor first loads to ensure we're comparing
   * "Quill HTML" to "Quill HTML" for accurate diffs.
   * Public so it can be passed to the diff-viewer component.
   */
  normalizedOriginal: string = '';

  /** Whether we've captured the normalized original yet */
  hasNormalizedOriginal: boolean = false;

  /** Computed HTML diff for display */
  diffHtml: SafeHtml = '';

  /** Whether there are unsaved changes */
  hasChanges: boolean = false;

  /** Splitter position as percentage */
  splitterPosition: number = 50;

  // ---- AI assistant state ----

  /** Whether the assistant button is shown (channel known, feature on for the org, editor writable). */
  assistEnabled: boolean = false;
  assistState: WritingAssistPanelState = 'closed';
  assistItems: ReadonlyArray<WritingAssistMenuItem> = WRITING_ASSIST_MENU;
  assistSelectedItem: WritingAssistMenuItem | null = null;
  assistInstruction: string = '';
  assistError: string = '';
  assistSlow: boolean = false;
  /** Null = whole message; otherwise the Quill range captured when the button was pressed. */
  assistScope: { index: number; length: number } | null = null;
  assistScopeWords: number = 0;
  /** Any text in scope (operations other than Help me write need some). */
  assistHasText: boolean = false;

  /**
   * The suggestion that was just written INTO the message (inline rewrite design): where it sits,
   * what produced it, and the word counts for the bar. Null when no result is pending a decision.
   */
  assistResult: {
    index: number; length: number;
    item: WritingAssistMenuItem; instruction: string;
    fromWords: number; toWords: number; model: string;
  } | null = null;
  /** Positions (px, relative to the edit pane) of the highlight box and the Keep / Undo bar. */
  assistHighlightBox: { top: number; left: number; width: number; height: number } | null = null;
  assistBarPos: { top: number; left: number } = { top: 0, left: 0 };
  assistTryMenuOpen: boolean = false;
  /** Shortcut hint for the Undo button. */
  readonly undoShortcut: string = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform) ? '⌘Z' : 'Ctrl+Z';

  private quill: QuillInstance | null = null;
  private assistButton: HTMLElement | null = null;
  private assistRequestToken: number = 0;
  private assistSlowTimer: ReturnType<typeof setTimeout> | null = null;
  /** True while the editor itself is writing a suggestion in (so the change is not read as the user typing). */
  private assistApplying: boolean = false;
  private assistScrollTarget: HTMLElement | null = null;
  private readonly onAssistScroll = () => this.positionAssistOverlay();

  /** Whether currently dragging the splitter */
  isDraggingSplitter: boolean = false;

  /** Quill editor configuration with WYSIWYG toolbar */
  quillModules = {
    toolbar: [
      ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
      ['blockquote', 'code-block'],                     // blocks
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],     // lists
      [{ 'header': [1, 2, 3, false] }],                 // headers
      ['link'],                                          // link
      ['clean']                                          // remove formatting
    ]
  };

  constructor(private sanitizer: DomSanitizer, private writingAssist: IzzyWritingAssistService) {}

  // ========== Lifecycle ==========

  ngOnInit(): void {
    this.editedContent = this.currentContent || this.originalContent || '';
    // An editor that starts empty (composers) has nothing to swallow as the "initial load".
    if (!this.editedContent) {
      this.normalizedOriginal = '';
      this.hasNormalizedOriginal = true;
      this.isFirstContentChange = false;
    }
    this.computeDiff();
    this.checkForChanges();
    this.refreshAssistEnabled();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentContent'] && !changes['currentContent'].firstChange) {
      this.editedContent = this.currentContent || '';
      this.computeDiff();
      this.checkForChanges();
    }
    if (changes['originalContent'] && !changes['originalContent'].firstChange) {
      this.computeDiff();
      this.checkForChanges();
    }
    if (changes['assistChannelId'] || changes['assistChannelMessageId'] || changes['readOnly']) {
      this.refreshAssistEnabled();
    }
  }

  ngOnDestroy(): void {
    this.assistRequestToken++;
    this.clearAssistSlowTimer();
    this.detachAssistScroll();
  }

  @HostListener('window:resize')
  onWindowResize(): void {
    if (this.assistResult) this.positionAssistOverlay();
  }

  // ========== Mode Switching ==========

  /** Set the editor mode */
  setMode(mode: EditorMode): void {
    // Leaving edit mode recreates the Quill instance; a pending inline result is kept as-is.
    if (mode !== 'edit') this.assistKeep();
    this.mode = mode;
    if (mode === 'diff' || mode === 'split') {
      this.computeDiff();
    }
  }

  /** Toggle expanded state */
  toggleExpanded(): void {
    this.isExpanded = !this.isExpanded;
    this.expandChange.emit(this.isExpanded);
  }

  // ========== Content Handling ==========

  /** Track if this is the very first content change (initial load) */
  private isFirstContentChange: boolean = true;

  /**
   * Called when Quill editor is created.
   */
  onEditorCreated(quill: QuillInstance): void {
    // Editor is ready, but content may not be loaded yet
    // We'll capture the normalized original on the first content change
    this.quill = quill;

    // Add tooltips to toolbar buttons
    this.addToolbarTooltips();
    this.installAssistButton();
  }

  /**
   * Add tooltips to Quill toolbar buttons
   */
  private addToolbarTooltips(): void {
    setTimeout(() => {
      const toolbarModule = this.quillEditor?.quillEditor?.getModule('toolbar');
      if (!toolbarModule) return;

      // Check if toolbar has a container property
      const toolbar = toolbarModule as { container?: HTMLElement };
      if (!toolbar.container) return;

      const container = toolbar.container;

      // Text formatting buttons
      const bold = container.querySelector('.ql-bold');
      if (bold) bold.setAttribute('title', 'Bold (Ctrl/Cmd+B)');

      const italic = container.querySelector('.ql-italic');
      if (italic) italic.setAttribute('title', 'Italic (Ctrl/Cmd+I)');

      const underline = container.querySelector('.ql-underline');
      if (underline) underline.setAttribute('title', 'Underline (Ctrl/Cmd+U)');

      const strike = container.querySelector('.ql-strike');
      if (strike) strike.setAttribute('title', 'Strikethrough');

      // Block formatting buttons
      const blockquote = container.querySelector('.ql-blockquote');
      if (blockquote) blockquote.setAttribute('title', 'Block Quote');

      const codeBlock = container.querySelector('.ql-code-block');
      if (codeBlock) codeBlock.setAttribute('title', 'Code Block');

      // List buttons
      const lists = container.querySelectorAll('.ql-list');
      lists.forEach((list) => {
        const value = list.getAttribute('value');
        if (value === 'ordered') {
          list.setAttribute('title', 'Numbered List');
        } else if (value === 'bullet') {
          list.setAttribute('title', 'Bulleted List');
        }
      });

      // Header dropdown
      const header = container.querySelector('.ql-header');
      if (header) header.setAttribute('title', 'Text Style (Heading/Normal)');

      // Link button
      const link = container.querySelector('.ql-link');
      if (link) link.setAttribute('title', 'Insert Link');

      // Clean formatting button
      const clean = container.querySelector('.ql-clean');
      if (clean) clean.setAttribute('title', 'Remove Formatting');
    }, 100);
  }

  /** Called when Quill editor content changes */
  onEditorContentChange(event: any): void {
    if (event.html !== undefined) {
      this.editedContent = event.html || '';

      // The FIRST content change is Quill loading the initial content
      // This is our baseline for comparison (Quill-normalized original)
      if (this.isFirstContentChange) {
        this.normalizedOriginal = this.editedContent;
        this.hasNormalizedOriginal = true;
        this.isFirstContentChange = false;
        // Don't emit change or compute diff on initial load
        return;
      }

      this.checkForChanges();

      // Typing over a pending suggestion keeps it (the bar goes away; Ctrl/Cmd+Z still works).
      if (this.assistResult && !this.assistApplying) {
        this.assistKeep();
      }

      // Re-compute diff if in split or diff mode
      if (this.mode === 'diff' || this.mode === 'split') {
        this.computeDiff();
      }

      this.contentChange.emit({
        content: this.editedContent,
        hasChanges: this.hasChanges
      });
    }
  }

  /** Check if content has changed from original */
  private checkForChanges(): void {
    const original = this.normalizeHtml(this.originalContent || '');
    const current = this.normalizeHtml(this.editedContent || '');
    this.hasChanges = original !== current;
  }

  /** Normalize HTML for comparison (strip whitespace, etc.) */
  private normalizeHtml(html: string): string {
    if (!html) return '';
    return html
      .replace(/>\s+</g, '><')
      .replace(/\s+/g, ' ')
      .trim();
  }

  /** Compute the HTML diff between original and edited content */
  private computeDiff(): void {
    // Use the Quill-normalized original if available, otherwise fall back to raw original
    const original = this.hasNormalizedOriginal ? this.normalizedOriginal : (this.originalContent || '');
    const edited = this.editedContent || '';

    if (!original && !edited) {
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml('');
      return;
    }

    try {
      // Use htmldiff-js to compute semantic HTML diff
      // Both sides should now be in the same "Quill HTML" format
      const diff = HtmlDiff.execute(original, edited);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(diff);
    } catch (error) {
      console.error('Error computing HTML diff:', error);
      this.diffHtml = this.sanitizer.bypassSecurityTrustHtml(edited);
    }
  }

  // ========== Host helpers (composers) ==========

  /**
   * Insert text (plain, newlines = paragraph breaks) or an HTML fragment at the caret, as one
   * undoable user edit. Used by the ticket composer's Macros.
   */
  insertAtCursor(content: string): void {
    const quill = this.quill;
    if (!quill || !content) return;
    const range = quill.getSelection(true) ?? { index: Math.max(0, quill.getLength() - 1), length: 0 };
    quill.history.cutoff();
    if (range.length > 0) {
      quill.deleteText(range.index, range.length, 'user');
    }
    if (ChannelMessageEntityExtendedClient.DetectMessageFormat(content) === 'HTML') {
      quill.clipboard.dangerouslyPasteHTML(range.index, content, 'user');
    } else {
      // Add a line break first when inserting mid-text so the macro starts on its own line.
      const prefix = range.index > 0 && quill.getText(range.index - 1, 1) !== '\n' ? '\n' : '';
      quill.insertText(range.index, prefix + content, 'user');
      quill.setSelection(range.index + prefix.length + content.length, 0, 'silent');
    }
    quill.history.cutoff();
  }

  /** Empty the editor (host just sent / submitted the body). */
  clear(): void {
    this.assistDiscard();
    this.assistKeep();
    this.editedContent = '';
    if (this.quill) {
      this.quill.setText('', 'user');
    }
    this.checkForChanges();
    this.computeDiff();
  }

  // ========== AI writing assistant ==========

  /** Button visible only when the channel is known, the org has the feature on, and the editor is writable. */
  private refreshAssistEnabled(): void {
    let enabled = !this.readOnly && (!!this.assistChannelId || !!this.assistChannelMessageId);
    if (enabled && this.assistChannelId) {
      try {
        enabled = IzzyMetadataEngine.Instance.GetSettingValueBoolean(WRITING_ASSISTANT_SETTING_KEY, this.assistChannelId, true);
      } catch {
        // Metadata engine not ready in this context: leave the button on; the server enforces the setting.
      }
    }
    this.assistEnabled = enabled;
    if (!enabled) {
      if (this.assistState !== 'closed') this.assistDiscard();
      if (this.assistResult) this.assistKeep();
    }
    this.syncAssistButton();
  }

  /**
   * Appends the sparkle button to Quill's toolbar. It is a plain DOM button (not a Quill format)
   * so Quill never tries to attach a handler to it; mousedown is prevented so the editor keeps
   * focus and the user's selection survives the click.
   */
  private installAssistButton(): void {
    setTimeout(() => {
      const toolbarModule = this.quillEditor?.quillEditor?.getModule('toolbar') as { container?: HTMLElement } | undefined;
      const container = toolbarModule?.container;
      if (!container || container.querySelector('.ql-izzy-assist')) {
        this.syncAssistButton();
        return;
      }
      const group = document.createElement('span');
      group.className = 'ql-formats izzy-assist-formats';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'ql-izzy-assist';
      button.setAttribute('title', 'Izzy writing assistant');
      button.setAttribute('aria-label', 'Izzy writing assistant');
      button.innerHTML = '<i class="fa-solid fa-wand-magic-sparkles"></i><span class="izzy-assist-label">AI</span>';
      button.addEventListener('mousedown', (e) => e.preventDefault());
      button.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.toggleAssistMenu();
      });
      group.appendChild(button);
      container.appendChild(group);
      this.assistButton = group;
      this.syncAssistButton();
    }, 100);
  }

  private syncAssistButton(): void {
    if (this.assistButton) {
      this.assistButton.style.display = this.assistEnabled ? '' : 'none';
    }
  }

  /** Words in a text (for the scope label). */
  private countWords(text: string): number {
    return text.trim().split(/\s+/).filter(w => w.length > 0).length;
  }

  get assistScopeLabel(): string {
    if (this.assistState === 'closed') return '';
    const words = `${this.assistScopeWords} word${this.assistScopeWords === 1 ? '' : 's'}`;
    return this.assistScope ? `Selected text (${words})` : `Whole message (${words})`;
  }

  /** Open the menu, capturing what the assistant will work on: the selection if there is one, else everything. */
  toggleAssistMenu(): void {
    if (this.assistState !== 'closed') {
      this.assistDiscard();
      return;
    }
    const quill = this.quill;
    if (!quill || !this.assistEnabled) return;
    if (this.assistResult) this.assistKeep();
    const range = quill.getSelection();
    if (range && range.length > 0) {
      this.assistScope = { index: range.index, length: range.length };
      this.assistScopeWords = this.countWords(quill.getText(range.index, range.length));
    } else {
      this.assistScope = null;
      this.assistScopeWords = this.countWords(quill.getText());
    }
    this.assistHasText = this.assistScopeWords > 0;
    this.assistSelectedItem = null;
    this.assistInstruction = '';
    this.assistError = '';
    this.assistState = 'menu';
  }

  assistPick(item: WritingAssistMenuItem): void {
    if (!this.assistHasText && !item.allowsEmptyText) return;
    this.assistSelectedItem = item;
    if (item.needsInstruction) {
      this.assistState = 'instruction';
      return;
    }
    void this.assistRun();
  }

  assistBackToMenu(): void {
    this.assistSelectedItem = null;
    this.assistState = 'menu';
  }

  /** The HTML the assistant works on: the captured selection, or the whole document. */
  private assistSourceHtml(): string {
    const quill = this.quill;
    if (!quill) return this.editedContent || '';
    if (this.assistScope) {
      return quill.getSemanticHTML(this.assistScope.index, this.assistScope.length);
    }
    return this.assistHasText ? quill.getSemanticHTML() : '';
  }

  /** The inbound message as plain text, capped, for "Help me write". */
  private assistPlainThreadContext(): string | null {
    const raw = this.assistThreadContext;
    if (!raw || !raw.trim()) return null;
    let text = raw;
    if (ChannelMessageEntityExtendedClient.DetectMessageFormat(raw) === 'HTML') {
      try {
        text = new DOMParser().parseFromString(raw, 'text/html').body.textContent || '';
      } catch {
        text = raw.replace(/<[^>]*>/g, ' ');
      }
    }
    return text.replace(/\s+\n/g, '\n').replace(/[ \t]+/g, ' ').trim().slice(0, WRITING_ASSIST_MAX_THREAD_CONTEXT_CHARS);
  }

  async assistRun(): Promise<void> {
    const item = this.assistSelectedItem;
    if (!item) return;
    const instruction = this.assistInstruction.trim();
    if (item.needsInstruction && !instruction) {
      this.assistState = 'instruction';
      return;
    }
    const token = ++this.assistRequestToken;
    this.assistState = 'running';
    this.assistError = '';
    this.assistSlow = false;
    this.clearAssistSlowTimer();
    this.assistSlowTimer = setTimeout(() => { if (token === this.assistRequestToken) this.assistSlow = true; }, 10_000);

    const sourceHtml = this.assistSourceHtml();
    const fromWords = this.assistScopeWords;
    const response = await this.writingAssist.assist({
      operation: item.operation,
      text: sourceHtml,
      instruction: item.needsInstruction ? instruction : undefined,
      channelMessageId: this.assistChannelMessageId,
      channelId: this.assistChannelId,
      threadContext: item.operation === 'write' ? this.assistPlainThreadContext() : null,
      format: 'html',
    });
    if (token !== this.assistRequestToken) return; // cancelled or superseded meanwhile
    this.clearAssistSlowTimer();
    this.assistSlow = false;
    if (!response.success || !response.text) {
      this.assistError = response.error || "Couldn't get a suggestion. Try again.";
      this.assistState = 'error';
      return;
    }
    this.applySuggestionInline(response.text, item, instruction, fromWords, response.model || '');
  }

  /** Error state's "Try again": same action, same scope. */
  assistRetry(): void {
    if (!this.assistSelectedItem) {
      this.assistState = 'menu';
      return;
    }
    void this.assistRun();
  }

  /**
   * Inline rewrite: write the suggestion INTO the message where the scope was (selection or the
   * whole body) as ONE user edit (history cutoff on each side, so a single Ctrl/Cmd+Z undoes it),
   * highlight the new text and show the Keep / Undo / Try again bar under it. Nothing is saved
   * until the host's own Save / Send.
   */
  private applySuggestionInline(html: string, item: WritingAssistMenuItem, instruction: string, fromWords: number, model: string): void {
    const quill = this.quill;
    if (!quill) return;
    const scope = this.assistScope;
    const lengthBefore = quill.getLength();
    let index = 0;
    let length = 0;
    this.assistApplying = true;
    try {
      quill.history.cutoff();
      let scopeLength = Math.max(0, lengthBefore - 1);
      if (scope) {
        index = Math.min(scope.index, Math.max(0, lengthBefore - 1));
        scopeLength = Math.min(scope.length, Math.max(0, lengthBefore - 1 - index));
        quill.deleteText(index, scopeLength, 'user');
        quill.clipboard.dangerouslyPasteHTML(index, html, 'user');
      } else {
        quill.clipboard.dangerouslyPasteHTML(html, 'user');
      }
      quill.history.cutoff();
      const lengthAfter = quill.getLength();
      length = scope ? lengthAfter - (lengthBefore - scopeLength) : Math.max(0, lengthAfter - 1);
      // A pasted fragment can end with a trailing newline Quill merges away; clamp to the document.
      length = Math.max(0, Math.min(length, lengthAfter - 1 - index));
    } finally {
      this.assistApplying = false;
    }
    this.assistResult = {
      index, length, item, instruction, fromWords, model,
      toWords: this.countWords(quill.getText(index, length)),
    };
    // Close the popover (the scope/item live on in the result for Try again) and place the bar once laid out.
    this.assistState = 'closed';
    this.assistTryMenuOpen = false;
    this.attachAssistScroll();
    quill.setSelection(index + length, 0, 'silent');
    setTimeout(() => {
      this.positionAssistOverlay(false);
      if (typeof requestAnimationFrame === 'function') {
        requestAnimationFrame(() => this.positionAssistOverlay(true));
      } else {
        this.positionAssistOverlay(true);
      }
    }, 0);
    setTimeout(() => this.positionAssistOverlay(false), 250);
  }

  /** Keep the suggestion: the highlight and the bar go away; the text stays (Ctrl/Cmd+Z still works). */
  assistKeep(): void {
    if (!this.assistResult) return;
    this.assistResult = null;
    this.assistHighlightBox = null;
    this.assistTryMenuOpen = false;
    this.detachAssistScroll();
  }

  /** Undo the suggestion: one history step back, which restores exactly what was there. */
  assistUndo(): void {
    const quill = this.quill;
    if (!quill || !this.assistResult) return;
    this.assistApplying = true;
    try {
      quill.history.undo();
    } finally {
      this.assistApplying = false;
    }
    this.assistKeep();
    quill.focus();
  }

  toggleAssistTryMenu(): void {
    this.assistTryMenuOpen = !this.assistTryMenuOpen;
  }

  /**
   * Try again on the text that was just written in: the same action, a quick variant (shorter,
   * warmer, more formal) or a custom instruction. Each attempt replaces the previous suggestion
   * as its own undo step.
   */
  assistTryAgain(variant: 'same' | 'shorten' | 'friendly' | 'professional' | 'custom'): void {
    const result = this.assistResult;
    const quill = this.quill;
    if (!result || !quill) return;
    this.assistTryMenuOpen = false;
    // The new scope is the inserted text.
    this.assistScope = { index: result.index, length: result.length };
    this.assistScopeWords = result.toWords;
    this.assistHasText = result.toWords > 0;
    const item = variant === 'same'
      ? result.item
      : this.assistItems.find(i => i.operation === variant) ?? result.item;
    this.assistSelectedItem = item;
    this.assistInstruction = variant === 'same' ? result.instruction : '';
    // Drop the pending result (the text stays; the retry writes over this range).
    this.assistResult = null;
    this.assistHighlightBox = null;
    this.detachAssistScroll();
    if (item.needsInstruction && !this.assistInstruction) {
      this.assistState = 'instruction';
      return;
    }
    void this.assistRun();
  }

  /** Place the highlight box over the inserted range and the bar just under it, clamped to the pane. */
  private positionAssistOverlay(scrollIntoView: boolean = false): void {
    const quill = this.quill;
    const result = this.assistResult;
    const pane = this.editorContainer?.nativeElement?.querySelector('.edit-pane.full') as HTMLElement | null;
    if (!quill || !result || !pane) return;
    const bounds = quill.getBounds(result.index, result.length);
    if (!bounds) return;

    const barEl = this.assistBarEl?.nativeElement;
    const barHeight = barEl && barEl.offsetHeight > 0 ? barEl.offsetHeight : 40;
    const barWidth = barEl && barEl.offsetWidth > 0 ? barEl.offsetWidth : 420;

    // In the main editor, <quill-editor class="quill-container"> is the scroll container (overflow-y: auto).
    const scrollContainer = pane.querySelector('.quill-container') as HTMLElement | null;
    if (scrollIntoView && scrollContainer && scrollContainer.scrollHeight > scrollContainer.clientHeight) {
      const preContainerRect = quill.container.getBoundingClientRect();
      const prePaneRect = pane.getBoundingClientRect();
      const preDy = preContainerRect.top - prePaneRect.top;
      const desiredBottom = preDy + bounds.bottom + 6 + barHeight + 8;
      if (desiredBottom > prePaneRect.height) {
        scrollContainer.scrollTop += desiredBottom - prePaneRect.height;
      } else {
        const preRootRect = quill.root.getBoundingClientRect();
        const preVisibleTop = Math.max(preRootRect.top, prePaneRect.top) - prePaneRect.top;
        if (preDy + bounds.top < preVisibleTop) {
          scrollContainer.scrollTop = Math.max(0, scrollContainer.scrollTop - (preVisibleTop - (preDy + bounds.top) + 8));
        }
      }
    }

    const containerRect = quill.container.getBoundingClientRect();
    const paneRect = pane.getBoundingClientRect();
    const dx = containerRect.left - paneRect.left;
    const dy = containerRect.top - paneRect.top;
    // Visible part of the editor (the Quill container scrolls inside .quill-container).
    const rootRect = quill.root.getBoundingClientRect();
    const visibleTop = Math.max(rootRect.top, paneRect.top) - paneRect.top;
    const visibleBottom = Math.min(rootRect.bottom, paneRect.bottom) - paneRect.top;
    const top = Math.max(dy + bounds.top, visibleTop);
    const bottom = Math.min(dy + bounds.bottom, visibleBottom);
    this.assistHighlightBox = bottom > top
      ? { top: top - 2, left: dx + bounds.left - 4, width: bounds.width + 8, height: bottom - top + 4 }
      : null;
    const barTop = Math.max(dy + bounds.bottom + 6, visibleTop + 4);
    // Keep the whole bar inside the pane horizontally (narrow composers): shift it left when needed.
    const maxLeft = Math.max(8, paneRect.width - barWidth - 8);
    this.assistBarPos = { top: barTop, left: Math.min(Math.max(8, dx + bounds.left), maxLeft) };
  }

  private attachAssistScroll(): void {
    this.detachAssistScroll();
    const scrollContainer = this.editorContainer?.nativeElement?.querySelector('.quill-container') as HTMLElement | null;
    const target = scrollContainer ?? this.quill?.root ?? null;
    if (!target) return;
    this.assistScrollTarget = target;
    target.addEventListener('scroll', this.onAssistScroll, { passive: true });
  }

  private detachAssistScroll(): void {
    if (this.assistScrollTarget) {
      this.assistScrollTarget.removeEventListener('scroll', this.onAssistScroll);
      this.assistScrollTarget = null;
    }
  }

  /** Close the popover (menu / instruction / running / error). A pending inline result is untouched. */
  assistDiscard(): void {
    this.assistRequestToken++;
    this.clearAssistSlowTimer();
    this.assistState = 'closed';
    this.assistSelectedItem = null;
    this.assistInstruction = '';
    this.assistError = '';
    this.assistSlow = false;
    this.assistScope = null;
  }

  private clearAssistSlowTimer(): void {
    if (this.assistSlowTimer) {
      clearTimeout(this.assistSlowTimer);
      this.assistSlowTimer = null;
    }
  }

  /** Revert to original content */
  revertToOriginal(): void {
    this.assistKeep();
    this.editedContent = this.originalContent || '';
    this.checkForChanges();
    this.computeDiff();
    this.revert.emit();

    // Update Quill editor if available
    if (this.quillEditor?.quillEditor) {
      const quill = this.quillEditor.quillEditor;
      quill.clipboard.dangerouslyPasteHTML(this.editedContent);
    }
  }

  // ========== Actions ==========

  /**
   * Clean nbsp from content - keep multiple for indents, replace single between words.
   * Quill's innerHTML renders ordinary spaces as &nbsp;, which makes sent emails unbreakable
   * (bug fixed 2026-09-15 on the approve path). Every path that SENDS editor HTML (approve
   * with edits, Compose New, ticket Reply) must run it through here; drafts kept for
   * re-editing stay raw so the diff baseline matches Quill's own output.
   */
  static CleanNbspForSend(content: string): string {
    // Replace isolated &nbsp; with a regular space.
    // Consecutive &nbsp; runs (indentation) are preserved because each
    // entity in a run is adjacent to another &nbsp;, failing the lookaround.
    return (content || '').replace(/(?<!&nbsp;)&nbsp;(?!&nbsp;)/g, ' ');
  }

  private cleanNbspForSave(content: string): string {
    return MessageEditorComponent.CleanNbspForSend(content);
  }

  /**
   * Save-and-approve from inside the editor was removed (product call 2026-08-25) — the
   * `save` output and the host's handler remain wired for now, but nothing invokes this.
   * Approval happens from the action bar after saving the draft.
   */
  approveWithEdits(): void {
    this.save.emit({
      content: this.cleanNbspForSave(this.editedContent),
      approved: true
    });
  }

  /** Save as draft without approving — preserve Quill HTML for re-editing */
  saveAsDraft(): void {
    this.saveDraft.emit(this.editedContent);
  }

  /** Cancel editing */
  cancelEdit(): void {
    this.cancel.emit();
  }

  // ========== Splitter Handling ==========

  /** Start dragging the splitter */
  startSplitterDrag(event: MouseEvent): void {
    event.preventDefault();
    this.isDraggingSplitter = true;
  }

  @HostListener('document:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDraggingSplitter || !this.editorContainer) return;

    const container = this.editorContainer.nativeElement;
    const rect = container.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const percentage = (x / rect.width) * 100;

    // Clamp between 20% and 80%
    this.splitterPosition = Math.max(20, Math.min(80, percentage));
  }

  @HostListener('document:mouseup')
  onMouseUp(): void {
    this.isDraggingSplitter = false;
  }

  // ========== Keyboard Shortcuts ==========

  @HostListener('document:keydown', ['$event'])
  handleKeyboardShortcut(event: KeyboardEvent): void {
    // Escape closes the assistant popover, or dismisses the inline bar (keeping the text)
    if (event.key === 'Escape' && (this.assistState !== 'closed' || this.assistResult)) {
      event.preventDefault();
      if (this.assistState !== 'closed') this.assistDiscard();
      else this.assistKeep();
      return;
    }

    // Only handle when editor is focused and expanded
    if (!this.isExpanded) return;

    // Escape to collapse
    if (event.key === 'Escape') {
      event.preventDefault();
      this.toggleExpanded();
    }

    // Ctrl/Cmd + S to save draft
    if ((event.ctrlKey || event.metaKey) && event.key === 's') {
      event.preventDefault();
      this.saveAsDraft();
    }

    // (Ctrl/Cmd + Enter approve removed 2026-08-25: approval lives outside the editor —
    // save the draft, exit, approve from the action bar.)
  }

  // ========== Utility ==========

  /** Get content for display (raw HTML or processed) */
  getDisplayContent(): string {
    return this.editedContent || '';
  }

  /**
   * Extract decoded plain text from Quill HTML so `&nbsp;` entities and HTML tags
   * do not glue words together or inflate the character count.
   */
  static ExtractPlainText(html: string): string {
    if (!html) return '';
    return html
      .replace(/<\/(p|div|li|h[1-6]|blockquote|pre|tr)>|<br\s*\/?>/gi, '\n')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;|&#160;|&#x0*a0;/gi, ' ')
      .replace(/&#(\d+);/g, (_m, dec) => {
        const code = Number(dec);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : _m;
      })
      .replace(/&#x([0-9a-f]+);/gi, (_m, hex) => {
        const code = parseInt(hex, 16);
        return Number.isFinite(code) && code > 0 && code <= 0x10ffff ? String.fromCodePoint(code) : _m;
      })
      .replace(/&quot;/gi, '"')
      .replace(/&#39;|&apos;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&amp;/gi, '&')
      .replace(/\n+$/, '');
  }

  /** Get word count of current content */
  getWordCount(): number {
    if (!this.editedContent) return 0;
    const text = MessageEditorComponent.ExtractPlainText(this.editedContent);
    return this.countWords(text);
  }

  /** Get character count of current content */
  getCharacterCount(): number {
    if (!this.editedContent) return 0;
    return MessageEditorComponent.ExtractPlainText(this.editedContent).length;
  }
}
